/**
 * English catalog — the owner assistant (/assistant).
 *
 * Split from en.ts like enBoards and enAgent, keeping every catalog file under
 * the 500-line fileSize ratchet. Spread into `en` in ./en.ts.
 */
export const enAssistant = {
  // ── Page ──────────────────────────────────────────────────────────────
  'assistant.title': 'Assistant',
  'assistant.subtitle': 'What happened, what needs you, and what to do next.',

  // ── Brief ─────────────────────────────────────────────────────────────
  'assistant.brief.heading': 'This morning',
  'assistant.brief.loading': 'Reading your business…',
  'assistant.brief.regenerate': 'Regenerate',
  'assistant.brief.regenerating': 'Regenerating…',
  'assistant.brief.failed': 'The brief could not be generated.',
  'assistant.brief.notConfigured':
    'No model key is set up yet. Add one in Settings below and the assistant can start work.',
  'assistant.brief.planRequired': 'Your plan has used its assistant allowance for today.',
  'assistant.brief.cached': 'Generated earlier today',
  'assistant.brief.fromTemplate':
    'Written from your numbers directly — the model’s draft was rejected for citing a figure that was not in your data.',

  // ── Facts ─────────────────────────────────────────────────────────────
  'assistant.facts.revenue7d': 'Revenue, 7 days',
  'assistant.facts.orders7d': 'Orders, 7 days',
  'assistant.facts.newCustomers': 'New customers, 7 days',
  'assistant.facts.subscribers': 'Reachable by email',
  'assistant.facts.unavailable': 'Not available',
  'assistant.facts.caveats': 'Worth knowing about these numbers',

  // ── Unfinished ────────────────────────────────────────────────────────
  'assistant.unfinished.heading': 'Started and not finished',
  'assistant.unfinished.empty': 'Nothing is half-finished. Unusual and good.',
  'assistant.unfinished.open': 'Finish this',

  // ── Chat ──────────────────────────────────────────────────────────────
  'assistant.chat.heading': 'Ask',
  'assistant.chat.placeholder': 'Ask about your business, or ask for something to be built…',
  'assistant.chat.send': 'Send',
  'assistant.chat.sending': 'Thinking…',
  'assistant.chat.empty': 'Ask a question, or ask for a draft.',
  'assistant.chat.failed': 'That did not go through.',
  'assistant.chat.rateLimited': 'That is the hourly limit. Try again shortly.',
  'assistant.chat.toolsUsed': 'Looked at',
  'assistant.chat.you': 'You',
  'assistant.chat.assistant': 'Assistant',

  // ── Pending confirmation ──────────────────────────────────────────────
  'assistant.pending.heading': 'Waiting for you',
  'assistant.pending.cancel': 'Cancel',
  'assistant.pending.confirmed': 'Sent.',
  'assistant.pending.cancelled': 'Cancelled. Nothing was sent.',
  'assistant.pending.expired': 'That confirmation expired. Ask again and it will be prepared fresh.',
  'assistant.pending.failed': 'That could not be confirmed.',
  'assistant.pending.chatHeading': 'Waiting for your approval',
  'assistant.pending.expiresIn': 'Expires in {minutes} min',
  'assistant.pending.expiresSoon': 'Expires in under a minute',
  'assistant.pending.busy': 'Working…',
  // A tool with no sentence of its own: named, never "Send it" for something that is not a send.
  'assistant.pending.generic': 'Approve “{tool}”?',
  'assistant.pending.genericExplain': 'The assistant asked to do this. Nothing happens until you approve it.',
  'assistant.pending.genericConfirm': 'Approve',
  'assistant.pending.genericDone': 'Done.',
  'assistant.pending.genericCancelled': 'Cancelled. Nothing happened.',
  // activate_popup: a sentence that names the popup, not a raw popupId and "Send it".
  'assistant.pending.popupLive': 'Turn on the popup “{name}”?',
  'assistant.pending.popupLiveUnnamed': 'Turn on this popup?',
  'assistant.pending.popupLiveExplain': 'Visitors who match its rules will start seeing it right away. You can pause it from Popups at any time.',
  'assistant.pending.popupLiveConfirm': 'Turn it on',
  'assistant.pending.popupLiveDone': 'Popup is on.',
  'assistant.pending.popupLiveCancelled': 'Cancelled. The popup was not switched on.',
  // send_campaign: the campaign's name and headcount, not a raw campaignId.
  'assistant.pending.campaignSendCount': 'Send “{name}” to {count} people?',
  'assistant.pending.campaignSendOne': 'Send “{name}” to 1 person?',
  'assistant.pending.campaignSendNamed': 'Send “{name}”?',
  'assistant.pending.campaignSendUnnamed': 'Send this campaign?',
  'assistant.pending.campaignSendExplain': 'Nothing goes out until you press Send. Once sent, it cannot be taken back.',
  'assistant.pending.campaignSendConfirm': 'Send',
  // send_campaign with no segment: the send would be refused (EMPTY_AUDIENCE), so the card says why and offers no Send.
  'assistant.pending.campaignNoAudience': '“{name}” has no audience, so it can’t be sent',
  'assistant.pending.campaignNoAudienceUnnamed': 'This campaign has no audience, so it can’t be sent',
  'assistant.pending.campaignNoAudienceExplain': 'It has no segment, so it would reach nobody. Open it in Campaigns, pick a segment, then ask the assistant to send it again. Cancel this request to clear it.',
  // /assistant/confirm — the deep link every host and the extension send the owner to.
  'assistant.confirm.title': 'Approve an assistant action',
  'assistant.confirm.lead': 'The assistant prepared this. It waits here until you decide.',
  'assistant.confirm.loading': 'Checking what is waiting…',
  'assistant.confirm.notFound': 'Nothing is waiting under that link. It may already be decided, cancelled, or expired.',
  'assistant.confirm.back': 'Back to the studio',

  // ── Butler (the morning greeting in the automations chat) ─────────────
  'butler.chatMoved': 'Talk to the assistant in the Chat tab under Studio.',
  'butler.pendingHeading': 'Waiting for you',
  'butler.builtHeading': 'I built these while you were away',
  'butler.panelToggle': 'What it has done, and what it may do',

  // ── The optional connect offer under the chat's greeting ──────────────
  // BuzzBit X runs no AI of its own: the chat answers only on the merchant's
  // own model. Connecting one stays optional for everything that is not AI —
  // the canvas works by hand.
  'connectChat.or': 'or',
  'connectChat.optional': 'Connecting Claude is optional — you can also build on the canvas by hand.',
  'butler.activityHeading': 'Recent activity',
  'butler.activityEmpty': 'Nothing yet.',
  'butler.accept': 'Keep it',
  'butler.dismiss': 'Not useful',

  // ── Settings ──────────────────────────────────────────────────────────
  'assistant.settings.heading': 'Settings',
  'assistant.settings.loading': 'Loading settings…',
  'assistant.settings.autonomy': 'How much it may do on its own',
  'assistant.settings.autonomy.L0': 'Read only — it answers, it never changes anything',
  'assistant.settings.autonomy.L1': 'Read and draft — it can build things, switched off',
  'assistant.settings.autonomy.L2': 'Read, draft, and schedule one message at a time',
  'assistant.settings.autonomyNote':
    'Sending to many people always waits for you, whatever this is set to.',
  'assistant.settings.browser': 'How far it may go on a page, in the Chrome panel',
  'assistant.settings.browser.B0': 'Read only — it can look at the page and never touch it',
  'assistant.settings.browser.B1': 'Read and move around — scroll and open pages',
  'assistant.settings.browser.B2': 'Fill things in — type into fields and click ordinary controls',
  'assistant.settings.browser.B3': 'Also submit, buy, publish and send — asking you every single time',
  'assistant.settings.browserNote':
    'This is the whole workspace, on every computer. Passwords, card numbers and one-time codes are refused at every setting, and payment, banking and sign-in sites are never operated at all.',
  'assistant.settings.mentor': 'How it frames advice',
  'assistant.settings.timezone': 'Time zone',
  'assistant.settings.country': 'Country',
  'assistant.settings.save': 'Save',
  'assistant.settings.saved': 'Saved.',
  'assistant.settings.saveFailed': 'Those settings were not saved.',

  // ── Credentials ───────────────────────────────────────────────────────
  // No AI of our own (owner decision 2026-10-08): there is no included model.
  // AI features run on the merchant's key or their own AI client over MCP.
  'assistant.key.heading': 'Your own model',
  'assistant.provider.heading': 'Which model answers',
  'assistant.provider.explain':
    'Use Claude, or point the assistant at any OpenAI-compatible endpoint — OpenRouter, Groq, OpenAI, or a model you run yourself. Everything else works the same either way: the same tools, the same confirmations, the same record of what was done.',
  'assistant.provider.anthropic': 'Claude',
  'assistant.provider.openaiCompatible': 'OpenAI-compatible',
  'assistant.provider.xai': 'xAI (Grok)',
  'assistant.provider.xaiHint': 'Answers through api.x.ai with a key from console.x.ai. Leave the model empty for the default.',
  'assistant.provider.xaiModelPlaceholder': 'grok-4.5',
  'assistant.provider.baseUrl': 'Endpoint URL',
  'assistant.provider.baseUrlPlaceholder': 'https://openrouter.ai/api/v1',
  'assistant.provider.baseUrlHint':
    'Must start with https, and be reachable from the internet — a model on your own machine needs a tunnel. Paste it with or without the trailing /v1.',
  'assistant.verify.button': 'Test this connection',
  'assistant.verify.testing': 'Testing…',
  'assistant.verify.explain':
    'Sends a real request that can only be answered by calling a tool. The assistant replies through tools, so an endpoint that cannot call them connects fine and then never answers a customer.',
  'assistant.verify.passed': 'Passed. This endpoint answers and can call tools.',
  'assistant.verify.connect': 'No model could be reached — save a key or endpoint of your own first.',
  'assistant.verify.request': 'The endpoint did not respond. Check the URL, that it is reachable from the internet, and the key.',
  'assistant.verify.tool_call': 'The model answered without calling the tool. Tool calling is off in your serving stack — on vLLM start it with --enable-auto-tool-choice and a tool-call parser.',
  'assistant.verify.tool_result': 'The endpoint rejected the tool result it was sent back.',
  'assistant.verify.use_result': 'The model called the tool and then ignored what it returned.',
  'assistant.verify.unknown': 'The test did not pass.',
  'assistant.verify.detail': 'Endpoint said: {detail}',
  'assistant.provider.model': 'Model',
  'assistant.provider.modelPlaceholder': 'meta-llama/llama-3.1-70b-instruct',
  // An arbitrary endpoint has no default the platform could know, so the
  // model is required for it (the server refuses MODEL_REQUIRED). xAI keeps
  // its own hint above: it has a default.
  'assistant.provider.modelHint': 'Required. The model id exactly as your endpoint lists it.',
  'assistant.provider.current': 'Answering now: {name}',
  'assistant.key.explainNone':
    'BuzzBit X runs no AI of its own. AI features need your own provider key for the model you pick below, or your own AI client connected through MCP.',
  'assistant.key.explainByo': 'Your key is in use: AI features run on your account.',
  'assistant.chat.connectExplain':
    'BuzzBit X runs no AI of its own. Add your own Anthropic key here so the assistant runs on your account, and you pay Anthropic directly for what it uses. To point it at a different model — OpenRouter, Groq, OpenAI, or one you run yourself — use the provider settings below the chat.',
  'assistant.key.selfHostedNote':
    'Bringing your own endpoint? It must be HTTPS and reachable from the internet — a model on your laptop (like Ollama on localhost) cannot be reached from here. To use a local model, connect it the other way around: it signs in to BuzzBit X as an app. See the developer docs.',
  'assistant.key.placeholder': 'sk-ant-…',
  'assistant.key.placeholderXai': 'xai-…',
  'assistant.key.placeholderOpenAi': 'sk-…',
  // Two sk-ant fields can sit on this screen at once — the connect card's and
  // this one. Neither had a label, so a screen reader announced them
  // identically and a merchant could type a key into the wrong box.
  'assistant.key.fieldLabel': 'Anthropic key for the assistant',
  // The key belongs to the provider picked above it, so its label names that
  // provider — an xAI key typed into a box labelled "Anthropic key" reads as a
  // mistake even when it is exactly right.
  'assistant.key.fieldLabelXai': 'xAI key for the assistant',
  'assistant.key.fieldLabelOpenAi': 'API key for your endpoint',
  'assistant.key.save': 'Save key',
  'assistant.key.remove': 'Remove',
  'assistant.key.configured': 'Configured',
  'assistant.key.masked': 'Configured · •••• {last4}',
  'assistant.key.none': 'Not set',
  'assistant.key.saveFailed': 'That key was not saved.',

  // Answer cards. The assistant replies with a card and the prose beneath it;
  // these are the only strings the cards add that are not from the server.
  'assistant.widget.confirmAction': 'Tap again to confirm',
  'assistant.widget.unnamedItem': 'Untitled',
  'assistant.widget.source': 'From',

  // ── Live activity (W4 client half): the ledger, arriving in time to act ──
  'assistant.live.parked': 'Assistant: {tool} is waiting for your approval',
  'assistant.live.succeeded': 'Assistant: {tool} — done',
  'assistant.live.failed': 'Assistant: {tool} failed',
  // Tool chip marker for L2/L3 calls — the server's tier, relayed on tool_start.
  'assistant.chip.reachesPeople': '· reaches people',
  'assistant.live.feedHeading': 'Assistant activity',
  'assistant.live.feedLive': 'live',
  'assistant.live.feedEmpty': 'Nothing yet — what the assistant does shows up here as it happens.',
  'assistant.live.needsApproval': 'needs approval',
  // What the assistant made, named — the ledger row knows its title now.
  'assistant.live.made': 'Assistant: {title} — done',
  'assistant.live.reverted': 'taken back',

  // ── Plans and tasks (P2): a multi-step job runs only after the owner approves ──
  'assistant.plan.heading': 'A plan, waiting for your approval',
  'assistant.plan.approve': 'Approve and run',
  'assistant.plan.notNow': 'Not now',
  'assistant.plan.busy': 'Working…',
  'assistant.plan.approved': 'Approved — running in the background.',
  'assistant.plan.declined': 'Set aside. Nothing ran.',
  'assistant.plan.failed': 'That plan could not be started.',
  'assistant.plan.stopsForYou': 'stops for you',
  'assistant.tasks.running': 'Running in the background',
  'assistant.tasks.done': 'Done.',
  'assistant.tasks.failed': 'Stopped — it could not finish.',
  'assistant.tasks.cancelled': 'Cancelled.',
  'assistant.tasks.waiting': 'Waiting for your approval on a step.',
  'assistant.tasks.progress': '{done} of {total} steps',
  'assistant.tasks.heading': 'Background tasks',
  'assistant.tasks.runningHeading': 'In progress',
  'assistant.tasks.empty': 'No background tasks yet — a job too big for one turn becomes one.',
  'assistant.tasks.cancel': 'Cancel',
  'assistant.tasks.cancelling': 'Cancelling…',
  'assistant.tasks.planned': 'Waiting for your approval',
  'assistant.tasks.queued': 'Queued',
  'assistant.tasks.showPlan': 'Show plan',
  'assistant.tasks.hidePlan': 'Hide plan',
  'assistant.tasks.live.done': 'Assistant: finished — {goal}',
  'assistant.tasks.live.failed': 'Assistant: could not finish — {goal}',
  'assistant.tasks.live.waiting': 'Assistant: a step of "{goal}" is waiting for your approval',
  'assistant.chat.capHit': 'That was as far as one turn goes — continue as a background task',
  'assistant.chat.capHitPrompt': 'Continue this as a background task: plan the rest with create_task.',

  // ── Undo (P2): a SUCCEEDED write, taken back from the ledger ──
  'assistant.undo.button': 'Undo',
  'assistant.undo.busy': 'Undoing…',
  'assistant.undo.done': 'Taken back.',
  'assistant.undo.failed': 'Could not take that back: {reason}',

  // ── Voice (V3): a faster way to type, never a way to approve ──
  'voice.startLabel': 'Hold to talk',
  'voice.stopLabel': 'Stop and transcribe',
  'voice.readAloud': 'Read aloud',
  'voice.reading': 'Reading…',
  'voice.echoNote': 'Heard — check it, then send.',
  'voice.readBrief': 'Read it to me',
  'voice.stopReading': 'Stop',
  'voice.noteHeading': 'Voice note',
  'voice.noteHint': 'Speak it, read it back, then save. Nothing is saved until you press save.',
  'voice.noteSave': 'Save note',
  'voice.noteSaving': 'Saving…',
  'voice.noteSaved': 'Note saved.',
  'voice.noteFailed': 'That note was not saved.',
  'voice.notePlaceholder': 'Your note…',

  // ── The studio chat's chrome (extracted from AutomationsChat) ──
  'assistant.chat.browseTemplates': 'Browse templates',
  'assistant.chat.model': 'Model:',
  'assistant.chat.disconnect': 'Disconnect',
  'assistant.chat.heroTitle': 'What should we automate?',
  // "your" Claude: BuzzBit X runs no AI of its own (same wording as the Lab's
  // Build page). Marketing to review wording.
  'assistant.chat.heroSubtitle': 'Describe it in a sentence — your Claude assembles the flow, you activate it. Rules run, nothing AI at runtime.',
  'assistant.chat.composerPlaceholder': 'Describe an automation… (Shift+Enter for newline)',
  'assistant.chat.stop': 'Stop',
  'assistant.chat.genericError': 'Something went wrong.',
  'assistant.chat.aiNotConfigured': 'This workspace has no AI of its own connected. Connect your AI through MCP to use the assistant.',

  // ── No AI of our own: shown where an AI action was hidden ─────────────
  'ai.byoHint': 'AI actions need your own AI.',
  'ai.byoHintLink': 'Connect your AI via MCP',
} as const;
